import { Button, Typography, FormHelperText, CircularProgress, Box } from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { putSignatureRequest, resetSignaturePut } from '../../../redux/modules/profile/slice';

// Flatten a (transparent) signature canvas onto an opaque white background. A bare
// canvas exports a transparent PNG whose dark strokes can vanish — reading as a
// blank/"white" image — once it's shown on a non-white surface or a downstream
// step drops its alpha channel. White-backing makes it render the same everywhere.
const toWhitePng = (src) => {
    const flat = document.createElement('canvas');
    flat.width = src.width || 400;
    flat.height = src.height || 200;
    const ctx = flat.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, flat.width, flat.height);
    ctx.drawImage(src, 0, 0);
    return flat.toDataURL('image/png');
};

const SignatureCanvas = ({ getValues, setValue, trigger, onSaved }) => {
    const dispatch = useDispatch();
    const { signatureLoading, putSignatureRes } = useSelector((state) => state.profile);
    const canvasRef = useRef(null);
    // Refs, not React state: the mousemove handler must read the latest value
    // synchronously. With useState the flag lags a render behind, so the opening
    // strokes are dropped and the saved signature can come out blank.
    const drawing = useRef(false);
    const hasInk = useRef(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [uploadError, setUploadError] = useState('');

    // Persist the signature immediately on its own request — no profile "Update"
    // click required. We also mirror it into the form's signatureImage field so a
    // subsequent full-profile save stays consistent.
    const persist = (dataUrl) => {
        setValue('signatureImage', dataUrl);
        if (trigger) trigger('signatureImage');
        setUploadError('');
        const fd = new FormData();
        fd.append('signatureImage', dataUrl);
        dispatch(putSignatureRequest(fd));
    };

    useEffect(() => {
        if (!putSignatureRes) return;
        if (putSignatureRes?.success) {
            if (onSaved) onSaved();
        } else {
            setUploadError(putSignatureRes?.message || 'Could not save the signature. Please try again.');
        }
        dispatch(resetSignaturePut());
    }, [putSignatureRes, onSaved, dispatch]);

    // Map an event's client coords to the canvas bitmap. Pointer events carry
    // clientX/clientY for mouse, pen and touch alike; offsetX/offsetY (the old
    // approach) is only populated for mouse, which is why pen/touch on a Surface
    // never drew. Scaling corrects any CSS↔bitmap size drift on smaller screens.
    const getPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = rect.width ? canvas.width / rect.width : 1;
        const scaleY = rect.height ? canvas.height / rect.height : 1;
        return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        // Capture so strokes keep tracking even if the pointer leaves the canvas.
        canvasRef.current.setPointerCapture?.(e.pointerId);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000';
        ctx.beginPath();
        const { x, y } = getPoint(e);
        ctx.moveTo(x, y);
        drawing.current = true;
    };

    const draw = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = getPoint(e);
        ctx.lineTo(x, y);
        ctx.stroke();
        if (!hasInk.current) {
            hasInk.current = true;
            setIsEmpty(false);
        }
    };

    const endDrawing = () => {
        drawing.current = false;
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        hasInk.current = false;
        setIsEmpty(true);
        setUploadError('');
    };

    const saveSignature = () => {
        // Don't store a blank pad — that's exactly what produces a "white" signature.
        if (!hasInk.current) {
            setUploadError('Please draw a signature first, or upload an image.');
            return;
        }
        persist(toWhitePng(canvasRef.current));
    };

    // Upload an existing signature image. We re-encode it to a PNG data URL (via
    // canvas) and persist it straight away. The white backing means a
    // transparent-PNG upload doesn't end up saved as blank.
    const handleUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = ['image/png', 'image/jpeg'];
        if (!allowed.includes(file.type)) {
            setUploadError('Please upload a PNG or JPEG image.');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setUploadError('Image is too large. Maximum size is 8MB.');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const c = document.createElement('canvas');
                c.width = img.naturalWidth || 400;
                c.height = img.naturalHeight || 200;
                const ctx = c.getContext('2d');
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, c.width, c.height);
                ctx.drawImage(img, 0, 0);
                persist(c.toDataURL('image/png'));
            };
            img.onerror = () => setUploadError('Could not read that image. Try a different file.');
            img.src = reader.result;
        };
        reader.onerror = () => setUploadError('Could not read that file. Try again.');
        reader.readAsDataURL(file);
    };

    return (
        <div>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Draw your signature below and click Save Signature, <b>or</b> upload an image. It saves on its own — no need to click Update.
            </Typography>
            <canvas
                style={{ border: "1px solid #000", touchAction: 'none' }}
                ref={canvasRef}
                width={400}
                height={200}
                onPointerDown={startDrawing}
                onPointerUp={endDrawing}
                onPointerMove={draw}
                onPointerLeave={endDrawing}
                onPointerCancel={endDrawing}
            />
            <div>
                <Button type='button' variant="contained" onClick={() => { clearCanvas() }} disabled={signatureLoading}>Reset</Button>
                <Button type='button' variant="contained" sx={{ ml: 1 }} onClick={() => { saveSignature() }} disabled={isEmpty || signatureLoading}>Save Signature</Button>
                <Button type='button' variant="outlined" component="label" sx={{ ml: 1 }} disabled={signatureLoading}>
                    Upload Image
                    <input type="file" hidden accept="image/png,image/jpeg" onChange={handleUpload} />
                </Button>
            </div>
            {signatureLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption">Saving signature…</Typography>
                </Box>
            )}
            {uploadError && <FormHelperText error>{uploadError}</FormHelperText>}
        </div>
    );
};

export default SignatureCanvas;
